/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { getScopedTheme } from 'src/core/themes/luisStylist';
import { getOrDefaultRegionFromResource } from 'src/core/utils/csAccountUtils';
import { ServicesTable } from 'src/luis/appShell/managePane/publishSettingsPage/ServicesTable';
import { PublishSlotCard } from 'src/luis/projects/publishPane/components/PublishSlotCard';
import { usePublishSettings } from 'src/luis/projects/publishPane/hooks/usePublishSettings';

const messages = defineMessages({
    pageTitle: {
        id: 'luis.publishSettingsPage.title',
        defaultMessage: 'Publish Settings'
    },
    instructions: {
        id: 'luis.publishSettingsPage.instructions',
        defaultMessage: `LUIS can use other services that affect the JSON response of your LUIS app. These services
affect the JSON response of your LUIS app. Changes to these settings go into effect only when you publish your app.
To accept changes in these settings, you must publish your app again`
    }
});

const theme = getScopedTheme('PublishSettingsPage');

/**
 * The component displaying the publish settings of a LUIS app
 */
export const PublishSettingsPage = observer(() => {
    const { localize } = useLocalization();
    const { rootStore } = useGlobalState();
    const { isXLargeOrGreater } = useResponsiveMode();
    const { productionSlot, stagingSLot } = usePublishSettings();

    const currentRegion = getOrDefaultRegionFromResource(rootStore.azureStore.session.csAccountStore);

    return (
        <Page size="container-fluid" pageName="PublishSettingsPage" title={localize(messages.pageTitle)}>
            <EditableTitle title={localize(messages.pageTitle)} />

            <Stack tokens={{ childrenGap: 50 }}>
                <LuisText lineHeight={theme.subtitleLineHeight}>{localize(messages.instructions)}</LuisText>

                <Stack horizontal={isXLargeOrGreater} tokens={{ childrenGap: isXLargeOrGreater ? 250 : 50 }}>
                    <PublishSlotCard currentRegion={currentRegion} isReadOnly slotCardData={{ ...stagingSLot, isStaging: true }} />
                    <PublishSlotCard currentRegion={currentRegion} isReadOnly slotCardData={{ ...productionSlot, isStaging: false }} />
                </Stack>

                <ServicesTable />
            </Stack>
        </Page>
    );
});
